import { QueryClient, QueryCache, type Query } from '@tanstack/react-query'
import { toast } from 'react-toastify'

// Window.queryClient type is declared in react-components/global.d.ts

const QUERY_STALE_TIME = 5 * 60 * 1000 // 5 minutes
const QUERY_GC_TIME = 10 * 60 * 1000 // 10 minutes
const DEFAULT_RETRY_COUNT = 1

/**
 * Singleton QueryClient for React Query
 *
 * Ensures single instance across all React apps mounted on the page.
 * Stored on window to share between multiple React roots (Magento pattern).
 *
 * Configuration:
 * - retry: 1 - Single retry on failure
 * - staleTime: 5 min - Data considered fresh for 5 minutes
 * - gcTime: 10 min - Unused data garbage collected after 10 minutes
 * - refetchOnWindowFocus: false - Don't refetch when tab regains focus
 */
export function getQueryClient(): QueryClient {
  if (window.queryClient) {
    return window.queryClient
  }

  window.queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error: Error, query: Query) => {
        // eslint-disable-next-line no-console -- Intentional error logging for debugging
        console.error('React Query error:', error)
        const errorMessage =
          (query?.meta?.errorMessage as string) ?? 'Something went wrong'
        toast.error(errorMessage)
      },
    }),
    defaultOptions: {
      queries: {
        retry: DEFAULT_RETRY_COUNT,
        staleTime: QUERY_STALE_TIME,
        gcTime: QUERY_GC_TIME,
        refetchOnWindowFocus: false,
      },
    },
  })

  return window.queryClient
}
